import makeStyles from '@mui/styles/makeStyles';
import { CustomColumn } from '../../../../../types/components/tables/tableTypes';
import { mainColors } from '../../../../../styling/theme';
import { FC } from 'react';
import { EditorTileProps } from '../../kids/editor/editorTiles/KidsEditorColumn.component';
import KidsEditorGridItem from '../../kidsV2/layout/KidsEditorGridItem.component';
import CustomTable from '../../../../tables/CustomTable';
import UtilityButtons from '../buttons/UtilityButtons.component';
import {
  addCommasToNumbersAndRound,
  percentageToTwoDecimalPlaces,
} from '../../../../../utilities/numberFormatters';

const useStyles = makeStyles(() => ({
  textEditorBox: {
    marginTop: '1.25rem',
    marginBottom: '1.25rem',
    marginLeft: '0.62rem',
    fontSize: '0.94rem',
    boxSizing: 'border-box',
    padding: '0.62rem',
    borderRadius: '6px',
    width: '100%',
    minHeight: '6.25rem',
  },
  editorContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    marginRight: '0.62rem',
  },
  formatContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'space-between',
  },
  buttonsContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    paddingLeft: '0.62rem',
    gap: '1.25rem',
  },
}));

interface CostsOverTimeTableData {
  scenario: string;
  valueYear1: string;
  valueHoldingPeriod: string;
}

function buildCostsOverTimeTableData(
  figures_data: any,
): CostsOverTimeTableData[] {
  const riy_dict = figures_data?.['riy_dict'] || {};
  const holding_period = figures_data?.['holding_period'];
  const currency = figures_data?.['currency'] || '';

  const getSafeCurrencyValue = (dict: any, key: string) => {
    const value = dict?.[key]?.[1];
    if (typeof value === 'number') {
      return `${currency} ${addCommasToNumbersAndRound(value)}`;
    } else {
      return 'Error';
    }
  };

  const getSafePercentageValue = (dict: any, key: string) => {
    const value = dict?.[key]?.[0];
    if (typeof value === 'number') {
      return `${percentageToTwoDecimalPlaces(value)} (each year)`;
    } else {
      return 'Error';
    }
  };

  return [
    {
      scenario: 'Total costs',
      valueYear1: getSafeCurrencyValue(riy_dict, '1'),
      valueHoldingPeriod: getSafeCurrencyValue(
        riy_dict,
        String(holding_period),
      ),
    },
    {
      scenario: 'Reduction in Yield (RIY) per year',
      valueYear1: getSafePercentageValue(riy_dict, '1'),
      valueHoldingPeriod: getSafePercentageValue(
        riy_dict,
        String(holding_period),
      ),
    },
  ];
}

function buildTableColumns(
  tableData: CostsOverTimeTableData[],
  figures_data: any,
): CustomColumn<CostsOverTimeTableData>[] {
  const holdingPeriod = figures_data['holding_period'];
  const currency = figures_data['currency'];
  const investmentAmount = figures_data['investment_amount'];

  return [
    {
      title: `Investment Scenarios ${currency} ${addCommasToNumbersAndRound(investmentAmount)}`,
      field: 'scenario',
      cellStyle: {
        width: '20%',
        border: '1px solid #1875d2',
        padding: '0.31rem',
        textAlign: 'center',
        backgroundColor: 'white',
      },
      headerStyle: {
        width: '20%',
        padding: '0.31rem',
        textAlign: 'center',
        color: 'white',
        backgroundColor: mainColors.controlButtonBlue,
      },
    },
    {
      title: 'If you cash in after 1 year',
      field: 'valueYear1',
      cellStyle: {
        border: '1px solid #1875d2',
        padding: '0.31rem',
        textAlign: 'center',
        backgroundColor: 'white',
      },
      headerStyle: {
        width: '20%',
        padding: '0.31rem',
        textAlign: 'center',
        color: 'white',
        backgroundColor: mainColors.controlButtonBlue,
      },
    },
    {
      title: `If you exit after ${String(holdingPeriod)} years`,
      field: 'valueHoldingPeriod',
      cellStyle: {
        border: '1px solid #1875d2',
        padding: '0.31rem',
        textAlign: 'center',
        backgroundColor: 'white',
      },
      headerStyle: {
        // border: '1px solid white',
        padding: '0.31rem',
        textAlign: 'center',
        color: 'white',
        backgroundColor: mainColors.controlButtonBlue,
      },
    },
  ];
}

const CostsOverTimeTableEditorTile: FC<EditorTileProps> = ({
  priipsKidsElement,
  moveField,
  deleteField,
}) => {
  // Store the index of the current element in the overall data
  const index = priipsKidsElement.kiidIndex;
  // Store the field ID of the current element
  const fieldId = priipsKidsElement.fieldId;
  // Convert the content to a javascript object
  const content = JSON.parse(priipsKidsElement.content);
  // Build the table data
  const tableData = buildCostsOverTimeTableData(content);
  // Build the table columns
  const columns = buildTableColumns(tableData, content);

  const classes = useStyles();

  return (
    <KidsEditorGridItem xs={12}>
      <div className={classes.editorContainer}>
        <div className={classes.textEditorBox}>
          <CustomTable<CostsOverTimeTableData>
            columns={columns}
            id={'performance_scenarios_table'}
            showToolbar={false}
            data={tableData}
            options={{
              paging: false,
              search: false,
              exportButton: false,
              sorting: false,
              draggable: false,
              toolbar: false,
            }}
          />
        </div>
        <UtilityButtons
          moveField={moveField}
          index={index}
          deleteField={deleteField}
          fieldId={fieldId}
          disableDelete
          disableDeleteMessage="You cannot delete the costs over time table"
        />
      </div>
    </KidsEditorGridItem>
  );
};

export default CostsOverTimeTableEditorTile;
